import type { DocPageContent } from '../docs-content'

const page = {
  order: 2,
  slug: 'workspaces',
  title: 'Espacios de trabajo y almacenamiento',
  description:
    'Cómo funciona la carpeta del espacio de trabajo, dónde se almacenan los archivos, cómo gestionar los permisos y mantener la caché.',
  category: 'Inicio',
  related: ['concepts', 'projects', 'troubleshooting'],
  sections: [
    {
      title: 'Cómo funciona un espacio de trabajo',
      blocks: [
        {
          kind: 'list',
          items: [
            'Un **espacio de trabajo** es una carpeta de tu equipo donde CachoCut guarda y administra toda la información relacionada con tus proyectos.',
            'Todo lo que genera CachoCut se almacena allí: proyectos, metadatos de los archivos multimedia, miniaturas, formas de onda, transcripciones, datos de escenas, recursos de IA, cachés y exportaciones.',
            'Los archivos multimedia originales **no se copian** al espacio de trabajo. CachoCut simplemente crea un enlace hacia la ubicación donde ya se encuentran almacenados en tu disco.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Como el espacio de trabajo es una carpeta normal de tu sistema, puedes hacer copias de seguridad, sincronizarla con otros servicios o moverla de ubicación cuando lo necesites.',
        },
      ],
    },
    {
      title: 'Elegir y cambiar de espacio de trabajo',
      blocks: [
        {
          kind: 'list',
          items: [
            'La primera vez que abras CachoCut, el programa te pedirá que selecciones un espacio de trabajo antes de acceder al editor.',
            'Elige una carpeta de uso personal, por ejemplo una ubicada dentro de **Documentos**. Evita utilizar carpetas protegidas del sistema.',
            'Utiliza el menú **Espacios de trabajo** para agregar nuevos espacios, cambiar entre ellos o eliminar los que ya no necesites.',
            'Al cambiar de espacio de trabajo, CachoCut simplemente pasa a utilizar otra carpeta. Tus archivos multimedia originales permanecen en su ubicación actual y no se mueven.',
          ],
        },
      ],
    },
    {
      title: 'Permisos y reconexión',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Por motivos de seguridad, puede revocar el permiso de acceso a una carpeta entre una sesión y otra.',
        },
        {
          kind: 'list',
          items: [
            'Si CachoCut muestra el mensaje **Reconecta tu espacio de trabajo**, haz clic en **Reconectar** y vuelve a conceder permisos de lectura y escritura para la misma carpeta.',
            'Si el acceso fue rechazado, vuelve a seleccionar la carpeta y acepta la solicitud de permisos cuando CachoCut lo indique.',
            'Si la carpeta fue movida, renombrada o eliminada, deberás seleccionar otra ubicación válida.',
          ],
        },
      ],
    },
    {
      title: 'Archivos multimedia no encontrados',
      blocks: [
        {
          kind: 'list',
          items: [
            '**Multimedia no encontrada** significa que CachoCut no puede acceder actualmente a uno o más archivos originales vinculados al proyecto.',
            'Utiliza **Conceder acceso** cuando el archivo sigue existiendo, pero CachoCut necesita que vuelvas a autorizar el acceso.',
            'Utiliza **Buscar archivo** o **Buscar carpeta** cuando el archivo o su carpeta fueron movidos a otra ubicación.',
            'Utiliza **Seleccionar otra carpeta** para indicar a CachoCut dónde se encuentra una nueva copia de los archivos multimedia.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Utiliza **Trabajar sin conexión** únicamente si tienes pensado volver a vincular los archivos más adelante. Mientras tanto, los clips permanecerán como referencias no disponibles.',
        },
      ],
    },
    {
      title: 'Mover proyectos y limpiar la caché',
      blocks: [
        {
          kind: 'list',
          items: [
            'Utiliza **Exportar proyecto** para crear un paquete portátil que incluya el proyecto y todos los archivos multimedia necesarios. Ese paquete podrá importarse posteriormente en otro espacio de trabajo.',
            'Las opciones de limpieza de la caché eliminan únicamente los datos que CachoCut puede volver a generar, como formas de onda, tiras de fotogramas, fotogramas GIF y audio decodificado.',
            'Desde la configuración de almacenamiento también puedes volver a generar miniaturas, crear los proxies que falten o eliminar los proxies existentes.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Limpiar la caché nunca elimina tus archivos multimedia originales. Si vuelven a ser necesarios, CachoCut regenerará automáticamente las vistas previas y los datos temporales.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page